import * as THREE from 'three';

/**
 * Wind Waker style wind effect using the thick lines/ribbons technique
 * Based on the approach described in the Wind Waker visual effects article
 */
export class WindEffect {
  constructor(scene, options = {}) {
    this.scene = scene;
    this.windLines = [];
    this.group = new THREE.Group();
    scene.add(this.group);
    
    // Load saved settings from localStorage if available
    let savedSettings = {};
    try {
      const savedData = localStorage.getItem('windEffectSettings');
      if (savedData) {
        savedSettings = JSON.parse(savedData);
      }
    } catch (e) {
      console.warn('Could not load saved wind effect settings', e);
    }
    
    // Default options
    this.options = {
      count: 1,                // Number of wind lines
      height: 20.0,            // Height above ground - now in the sky
      minRadius: 5,            // Minimum radius for spiral
      maxRadius: 30,           // Maximum radius for spiral
      lineWidth: 0.34,         // Width of the wind lines
      lineColor: 0xffffff,     // Color of wind lines
      opacity: 0.25,           // Opacity of wind lines
      rotationSpeed: 0.002,    // How fast the entire effect rotates
      animationSpeed: 0.01,    // Base animation speed
      pattern: 'linear',       // Wind pattern: 'circular' or 'linear'
      ...savedSettings,        // Override with saved settings
      ...options               // Override with any provided options
    };
    
    // Position the entire group in the sky
    this.group.position.y = this.options.height;
    
    this.createWindLines();
    
    // Initialize the control panel
    this.initControlPanel();
  }
  
  /**
   * Create all wind line objects
   */
  createWindLines() {
    // Clear any existing wind lines
    this.windLines.forEach(line => {
      if (line.mesh) {
        this.group.remove(line.mesh);
        if (line.mesh.geometry) line.mesh.geometry.dispose();
        if (line.mesh.material) line.mesh.material.dispose();
      }
    });
    
    this.windLines = [];
    
    // Create new wind lines
    for (let i = 0; i < this.options.count; i++) {
      // Create with slight variations for each line
      const line = this.createWindLine({
        radius: this.options.minRadius + Math.random() * (this.options.maxRadius - this.options.minRadius),
        width: this.options.lineWidth * (0.7 + Math.random() * 0.6), // Vary width slightly
        animationOffset: Math.random(), // Random starting point in animation
        animationSpeed: this.options.animationSpeed * (0.8 + Math.random() * 0.4), // Vary speed slightly
        rotationOffset: Math.random() * Math.PI * 2 // Random rotation
      });
      
      this.windLines.push(line);
      this.group.add(line.mesh);
    }
  }
  
  /**
   * Create a single wind line using the thick line technique
   */
  createWindLine(options) {
    // Create points for the wind line based on the selected pattern
    const points = [];
    const segments = 8; // Low poly count for Wind Waker style
    const radius = options.radius;
    const curveAmount = 0.2; // How much the line curves
    
    if (this.options.pattern === 'circular') {
      // Create a circular/spiral pattern (original style)
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const angle = t * Math.PI * (1 + Math.random() * 0.2); // Slight randomness in length
        const x = Math.cos(angle) * radius * (1 - t * 0.3); // Spiral inward slightly
        const z = Math.sin(angle) * radius * (1 - t * 0.3);
        // No need to add height since the entire group is positioned in the sky
        const y = Math.sin(t * Math.PI) * curveAmount; // Gentle up and down
        
        points.push(new THREE.Vector3(x, y, z));
      }
    } else {
      // Create a linear left-to-right pattern with random starting positions
      const lineLength = radius * 1.5; // Use radius to determine length
      
      // Randomize the starting position - some lines start from left, some from right
      // and some in the middle with different lengths
      const randomOffset = (Math.random() - 0.5) * lineLength * 1.5;
      const randomLength = lineLength * (0.7 + Math.random() * 0.6); // Vary length slightly
      
      const startX = randomOffset - randomLength / 2;
      const endX = randomOffset + randomLength / 2;
      
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = startX + t * randomLength;
        
        // Add gentle wave pattern with more variation
        const waveAmplitude = lineLength * (0.05 + Math.random() * 0.15); // Random wave height
        const waveFrequency = 1 + Math.random() * 3; // Random wave frequency
        const wavePhase = Math.random() * Math.PI * 2; // Random phase offset
        const z = Math.sin(t * Math.PI * waveFrequency + wavePhase) * waveAmplitude;
        
        // Vary height slightly but keep it in the sky plane
        // No need to add this.options.height since the entire group is positioned in the sky
        const heightVariation = Math.random() * 0.4;
        const y = heightVariation + Math.sin(t * Math.PI) * curveAmount * 0.5;
        
        points.push(new THREE.Vector3(x, y, z));
      }
    }
    
    // Create a curve from the points
    const curve = new THREE.CatmullRomCurve3(points);
    
    // Generate the thick line geometry
    const geometry = this.createThickLineGeometry(curve, options.width);
    
    // Create material with custom shader for the wind effect
    const material = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: new THREE.Color(this.options.lineColor) },
        opacity: { value: this.options.opacity },
        time: { value: options.animationOffset || 0 }
      },
      vertexShader: `
        varying vec2 vUv;
        
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float opacity;
        uniform float time;
        varying vec2 vUv;
        
        void main() {
          // Create a very visible moving dash pattern
          float speed = 5.0; // Increase this for faster animation
          float dashSize = 0.2;
          float gapSize = 0.3;
          float totalSize = dashSize + gapSize;
          
          // Create a moving pattern along the ribbon
          // Using positive time value for right-to-left movement
          float t = mod(vUv.x + time * speed, totalSize);
          float dash = step(t, dashSize);
          
          // Make the dash more visible with a gradient
          dash *= smoothstep(0.0, 0.5, 1.0 - abs(t / dashSize - 0.5) * 2.0);
          
          // Add edge fading
          float edgeFade = smoothstep(0.0, 0.2, vUv.y) * smoothstep(1.0, 0.8, vUv.y);
          edgeFade *= smoothstep(0.0, 0.1, vUv.x) * smoothstep(1.0, 0.9, vUv.x);
          
          gl_FragColor = vec4(color, dash * opacity * edgeFade);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.y = options.rotationOffset || 0;
    
    return {
      mesh,
      options,
      update: (deltaTime) => {
        // Update the time uniform to animate the texture
        mesh.material.uniforms.time.value += deltaTime * options.animationSpeed;
        
        // Rotate the mesh slightly
        mesh.rotation.y += deltaTime * this.options.rotationSpeed;
      }
    };
  }
  
  /**
   * Create a thick line geometry from a curve
   * Creates horizontal ribbons visible from top-down camera
   */
  createThickLineGeometry(curve, width) {
    const geometry = new THREE.BufferGeometry();
    const divisions = curve.points.length * 3; // Smooth the curve
    const curvePoints = curve.getPoints(divisions);

    const vertices = [];
    const uvs = [];
    const halfWidth = width / 2;

    // For each point in the curve, create two vertices offset perpendicular to the curve direction
    for (let i = 0; i < curvePoints.length; i++) {
      const point = curvePoints[i];
      const t = i / (curvePoints.length - 1); // Normalized position along curve

      // Calculate tangent direction for perpendicular offset
      let tangent;
      if (i < curvePoints.length - 1) {
        tangent = new THREE.Vector3().subVectors(curvePoints[i + 1], point).normalize();
      } else {
        tangent = new THREE.Vector3().subVectors(point, curvePoints[i - 1]).normalize();
      }

      // Get perpendicular direction in XZ plane (horizontal ribbon)
      const perpendicular = new THREE.Vector3(-tangent.z, 0, tangent.x).normalize();

      // Create two vertices offset perpendicular to curve in XZ plane
      vertices.push(
        point.x + perpendicular.x * halfWidth, point.y, point.z + perpendicular.z * halfWidth,
        point.x - perpendicular.x * halfWidth, point.y, point.z - perpendicular.z * halfWidth
      );

      // UVs for texture mapping
      uvs.push(
        t, 0,
        t, 1
      );
    }

    // Create faces by connecting the vertices
    const indices = [];
    for (let i = 0; i < curvePoints.length - 1; i++) {
      const a = i * 2;
      const b = a + 1;
      const c = a + 2;
      const d = a + 3;

      // Create two triangles for each segment
      indices.push(a, b, c);
      indices.push(c, b, d);
    }

    // Set the attributes
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);

    return geometry;
  }
  
  /**
   * Update wind animation
   */
  update(deltaTime) {
    // Ensure we have a valid delta time
    if (!deltaTime) deltaTime = 0.016; // Default to 60fps if no deltaTime provided
    
    // Skip if not active
    if (this.options.active === false) return;
    
    // Apply rotation based on pattern
    if (this.options.pattern === 'circular') {
      this.group.rotation.y += this.options.rotationSpeed * deltaTime;
    } else {
      this.group.rotation.y = 0; // Keep linear pattern aligned with world axes
    }
    
    // Direct animation of each wind line
    for (let i = 0; i < this.windLines.length; i++) {
      const line = this.windLines[i];
      if (!line.mesh || !line.mesh.material || !line.mesh.material.uniforms) continue;
      
      // Get speed based on pattern
      let speed = this.options.animationSpeed;
      if (this.options.pattern === 'linear') {
        // For linear pattern, use positive speed for right-to-left movement
        speed *= 1.5;
        line.mesh.material.uniforms.time.value += speed * deltaTime;
      } else {
        // For circular pattern, keep the original direction
        line.mesh.material.uniforms.time.value -= speed * deltaTime;
      }
      
      // Add individual rotation for circular pattern
      if (this.options.pattern === 'circular') {
        line.mesh.rotation.y += this.options.rotationSpeed * 0.5 * deltaTime;
      }
    }
    
    // Force the effect to be active by default
    this.options.active = true;
  }
  
  /**
   * Set visibility of the wind effect
   */
  setVisible(visible) {
    this.group.visible = visible;
  }
  
  /**
   * Initialize the control panel for adjusting wind effect settings
   */
  initControlPanel() {
    // Get control panel elements
    this.controlPanel = document.getElementById('wind-control-panel');
    const toggleButton = document.getElementById('wind-control-toggle');
    const applyButton = document.getElementById('wind-apply');
    const saveButton = document.getElementById('wind-save');
    const resetButton = document.getElementById('wind-reset');
    
    // Set initial values from current options
    document.getElementById('wind-count').value = this.options.count;
    document.getElementById('wind-count').nextElementSibling.textContent = this.options.count;
    
    document.getElementById('wind-opacity').value = this.options.opacity;
    document.getElementById('wind-opacity').nextElementSibling.textContent = this.options.opacity;
    
    document.getElementById('wind-width').value = this.options.lineWidth;
    document.getElementById('wind-width').nextElementSibling.textContent = this.options.lineWidth;
    
    document.getElementById('wind-speed').value = this.options.animationSpeed;
    document.getElementById('wind-speed').nextElementSibling.textContent = this.options.animationSpeed;
    
    document.getElementById('wind-rotation').value = this.options.rotationSpeed;
    document.getElementById('wind-rotation').nextElementSibling.textContent = this.options.rotationSpeed;
    
    // Convert hex color to HTML color input format
    const colorHex = '#' + this.options.lineColor.toString(16).padStart(6, '0');
    document.getElementById('wind-color').value = colorHex;
    
    // Set the pattern dropdown
    document.getElementById('wind-pattern').value = this.options.pattern;
    
    // Make the panel draggable
    this.makeDraggable(this.controlPanel);
    
    // Toggle panel collapse/expand
    toggleButton.addEventListener('click', () => {
      this.controlPanel.classList.toggle('collapsed');
    });
    
    // Update value displays when sliders change
    const sliders = this.controlPanel.querySelectorAll('input[type="range"]');
    sliders.forEach(slider => {
      slider.addEventListener('input', () => {
        slider.nextElementSibling.textContent = slider.value;
      });
    });
    
    // Apply button - update settings without saving
    applyButton.addEventListener('click', () => {
      this.updateSettingsFromControls();
      this.createWindLines(); // Recreate wind lines with new settings
    });
    
    // Save button - update settings and save to localStorage
    saveButton.addEventListener('click', () => {
      this.updateSettingsFromControls();
      this.saveSettings();
      this.createWindLines(); // Recreate wind lines with new settings
      this.showNotification('Wind effect settings saved', 1500);
    });
    
    // Reset button - restore default settings
    resetButton.addEventListener('click', () => {
      this.resetSettings();
      this.createWindLines(); // Recreate wind lines with default settings
    });
    
    // Start with collapsed panel if not first time
    if (localStorage.getItem('windEffectSettings')) {
      this.controlPanel.classList.add('collapsed');
    }
  }
  
  /**
   * Make an element draggable
   */
  makeDraggable(element) {
    const header = element.querySelector('.control-header');
    let isDragging = false;
    let offsetX, offsetY;
    
    header.addEventListener('mousedown', (e) => {
      isDragging = true;
      offsetX = e.clientX - element.getBoundingClientRect().left;
      offsetY = e.clientY - element.getBoundingClientRect().top;
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      
      const x = e.clientX - offsetX;
      const y = e.clientY - offsetY;
      
      // Keep within window bounds
      const maxX = window.innerWidth - element.offsetWidth;
      const maxY = window.innerHeight - element.offsetHeight;
      
      element.style.left = Math.max(0, Math.min(x, maxX)) + 'px';
      element.style.top = Math.max(0, Math.min(y, maxY)) + 'px';
      element.style.right = 'auto';
    });
    
    document.addEventListener('mouseup', () => {
      isDragging = false;
    });
  }
  
  /**
   * Update settings from control panel values
   */
  updateSettingsFromControls() {
    this.options.count = parseInt(document.getElementById('wind-count').value);
    this.options.opacity = parseFloat(document.getElementById('wind-opacity').value);
    this.options.lineWidth = parseFloat(document.getElementById('wind-width').value);
    this.options.animationSpeed = parseFloat(document.getElementById('wind-speed').value);
    this.options.rotationSpeed = parseFloat(document.getElementById('wind-rotation').value);
    
    // Convert HTML color to hex number
    const colorHex = document.getElementById('wind-color').value;
    this.options.lineColor = parseInt(colorHex.substring(1), 16);
    
    // Get the wind pattern
    this.options.pattern = document.getElementById('wind-pattern').value;
  }
  
  /**
   * Save current settings to localStorage
   */
  saveSettings() {
    try {
      localStorage.setItem('windEffectSettings', JSON.stringify(this.options));
    } catch (e) {
      console.warn('Could not save wind effect settings', e);
    }
  }
  
  /**
   * Reset settings to defaults
   */
  resetSettings() {
    // Default settings
    const defaults = {
      count: 1,
      height: 20.0,
      minRadius: 5,
      maxRadius: 30,
      lineWidth: 0.34,
      lineColor: 0xffffff,
      opacity: 0.25,
      rotationSpeed: 0.002,
      animationSpeed: 0.01,
      pattern: 'linear'
    };
    
    // Apply defaults
    Object.assign(this.options, defaults);
    
    // Update control panel values
    document.getElementById('wind-count').value = defaults.count;
    document.getElementById('wind-count').nextElementSibling.textContent = defaults.count;
    
    document.getElementById('wind-opacity').value = defaults.opacity;
    document.getElementById('wind-opacity').nextElementSibling.textContent = defaults.opacity;
    
    document.getElementById('wind-width').value = defaults.lineWidth;
    document.getElementById('wind-width').nextElementSibling.textContent = defaults.lineWidth;
    
    document.getElementById('wind-speed').value = defaults.animationSpeed;
    document.getElementById('wind-speed').nextElementSibling.textContent = defaults.animationSpeed;
    
    document.getElementById('wind-rotation').value = defaults.rotationSpeed;
    document.getElementById('wind-rotation').nextElementSibling.textContent = defaults.rotationSpeed;
    
    document.getElementById('wind-color').value = '#ffffff';
    document.getElementById('wind-pattern').value = defaults.pattern;
    
    // Remove saved settings
    localStorage.removeItem('windEffectSettings');
    
    this.showNotification('Wind effect settings reset', 1500);
  }
  
  /**
   * Show a temporary notification message
   */
  showNotification(message, duration = 2000) {
    // Check if notification container exists, create if not
    let notificationContainer = document.getElementById('wind-notifications');
    if (!notificationContainer) {
      notificationContainer = document.createElement('div');
      notificationContainer.id = 'wind-notifications';
      notificationContainer.style.position = 'fixed';
      notificationContainer.style.bottom = '20px';
      notificationContainer.style.right = '20px';
      notificationContainer.style.zIndex = '1000';
      document.body.appendChild(notificationContainer);
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'wind-notification';
    notification.textContent = message;
    notification.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    notification.style.color = 'white';
    notification.style.padding = '10px 15px';
    notification.style.borderRadius = '4px';
    notification.style.marginTop = '10px';
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s ease';
    
    // Add to container
    notificationContainer.appendChild(notification);
    
    // Fade in
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    // Remove after duration
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, duration);
  }
  
  /**
   * Clean up resources
   */
  dispose() {
    this.windLines.forEach(line => {
      if (line.mesh) {
        this.group.remove(line.mesh);
        if (line.mesh.geometry) line.mesh.geometry.dispose();
        if (line.mesh.material) line.mesh.material.dispose();
      }
    });
    
    this.scene.remove(this.group);
  }
}
